import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Activity, 
  Heart, 
  TrendingUp, 
  Download, 
  Upload,
  Plus,
  Calendar,
  Target,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { mockVitals, mockUser } from '../mock/mockData';

const Vitals = () => {
  const [newVital, setNewVital] = useState({
    kind: '',
    value: '',
    unit: '',
    measuredAt: new Date().toISOString().split('T')[0]
  });
  const [vitals, setVitals] = useState(mockVitals);
  const [timeRange, setTimeRange] = useState('30');

  // Calculate BMI
  const height = vitals.find(v => v.kind === 'height')?.value || 170;
  const weight = vitals.find(v => v.kind === 'weight')?.value || 70;
  const bmi = (weight / Math.pow(height / 100, 2)).toFixed(1);

  const getBMICategory = (bmi) => {
    if (bmi < 18.5) return { category: 'Underweight', color: 'text-blue-600', bg: 'bg-blue-100' };
    if (bmi < 25) return { category: 'Normal', color: 'text-green-600', bg: 'bg-green-100' };
    if (bmi < 30) return { category: 'Overweight', color: 'text-yellow-600', bg: 'bg-yellow-100' };
    return { category: 'Obese', color: 'text-red-600', bg: 'bg-red-100' };
  };

  const bmiCategory = getBMICategory(parseFloat(bmi));

  const vitalTypes = [
    { value: 'blood_pressure_systolic', label: 'Blood Pressure (Systolic)', unit: 'mmHg' },
    { value: 'blood_pressure_diastolic', label: 'Blood Pressure (Diastolic)', unit: 'mmHg' },
    { value: 'blood_sugar_fasting', label: 'Blood Sugar (Fasting)', unit: 'mg/dL' },
    { value: 'cholesterol_total', label: 'Total Cholesterol', unit: 'mg/dL' },
    { value: 'weight', label: 'Weight', unit: 'kg' },
    { value: 'height', label: 'Height', unit: 'cm' },
    { value: 'hemoglobin', label: 'Hemoglobin', unit: 'g/dL' }
  ];

  const addVital = (e) => {
    e.preventDefault();
    if (newVital.kind && newVital.value) {
      const vital = {
        id: Date.now(),
        kind: newVital.kind,
        value: parseFloat(newVital.value),
        unit: vitalTypes.find(vt => vt.value === newVital.kind)?.unit || newVital.unit,
        measuredAt: new Date(newVital.measuredAt)
      };
      setVitals([...vitals, vital]);
      setNewVital({
        kind: '',
        value: '',
        unit: '',
        measuredAt: new Date().toISOString().split('T')[0]
      });
    }
  };

  const getLatestVital = (type) => {
    return vitals
      .filter(v => v.kind === type)
      .sort((a, b) => new Date(b.measuredAt) - new Date(a.measuredAt))[0];
  };

  const getVitalStatus = (type, value) => {
    const ranges = {
      blood_pressure_systolic: { normal: [90, 120], high: [120, 140] },
      blood_pressure_diastolic: { normal: [60, 80], high: [80, 90] },
      blood_sugar_fasting: { normal: [70, 100], high: [100, 126] },
      cholesterol_total: { normal: [0, 200], high: [200, 240] }
    };

    const range = ranges[type];
    if (!range) return { status: 'normal', color: 'text-gray-600' };

    if (value >= range.normal[0] && value <= range.normal[1]) {
      return { status: 'normal', color: 'text-green-600', icon: CheckCircle };
    } else if (value > range.high[1]) {
      return { status: 'high', color: 'text-red-600', icon: AlertTriangle };
    } else if (value > range.normal[1]) {
      return { status: 'borderline', color: 'text-yellow-600', icon: AlertTriangle };
    }
    return { status: 'low', color: 'text-blue-600', icon: AlertTriangle };
  };

  const exportToCSV = () => {
    const csv = [
      ['Kind', 'Value', 'Unit', 'Measured At'],
      ...vitals.map(v => [v.kind, v.value, v.unit, v.measuredAt])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'vitals-data.csv';
    a.click();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Health Vitals</h1>
              <p className="text-xl text-gray-600 mt-2">Track and monitor your health metrics</p>
            </div>
            <div className="flex space-x-3">
              <Button variant="outline" onClick={exportToCSV}>
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              <Button variant="outline">
                <Upload className="w-4 h-4 mr-2" />
                Import CSV
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* BMI Card */}
            <Card className="border-orange-200">
              <CardHeader className="bg-orange-50">
                <CardTitle className="flex items-center space-x-2">
                  <Target className="w-5 h-5 text-orange-600" />
                  <span>Body Mass Index (BMI)</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-gray-900 mb-2">{bmi}</div>
                    <Badge className={`${bmiCategory.bg} ${bmiCategory.color} text-sm px-3 py-1`}>
                      {bmiCategory.category}
                    </Badge>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Height:</span>
                      <span className="font-medium">{height} cm</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Weight:</span>
                      <span className="font-medium">{weight} kg</span>
                    </div>
                  </div>
                  <div className="text-xs text-gray-600">
                    <p><strong>Normal:</strong> 18.5 - 24.9</p>
                    <p><strong>Overweight:</strong> 25.0 - 29.9</p>
                    <p><strong>Obese:</strong> 30.0+</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Key Vitals Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {['blood_pressure_systolic', 'blood_sugar_fasting', 'cholesterol_total', 'hemoglobin'].map((vitalType) => {
                const latest = getLatestVital(vitalType);
                const status = latest ? getVitalStatus(vitalType, latest.value) : null;
                const vitalInfo = vitalTypes.find(vt => vt.value === vitalType);
                
                return (
                  <Card key={vitalType} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-gray-900">{vitalInfo?.label}</h3>
                        {status && status.icon && (
                          <status.icon className={`w-5 h-5 ${status.color}`} />
                        )}
                      </div>
                      {latest ? (
                        <div>
                          <div className="flex items-baseline space-x-2 mb-2">
                            <span className="text-3xl font-bold text-gray-900">{latest.value}</span>
                            <span className="text-sm text-gray-500">{latest.unit}</span>
                          </div>
                          <p className="text-sm text-gray-600">
                            {new Date(latest.measuredAt).toLocaleDateString()}
                          </p>
                          {status && (
                            <Badge className={`mt-2 ${status.color} bg-transparent border`}>
                              {status.status}
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <div className="text-gray-400">
                          <p>No data recorded</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Vitals History */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center space-x-2">
                    <Activity className="w-5 h-5 text-blue-600" />
                    <span>Vitals History</span>
                  </CardTitle>
                  <Select value={timeRange} onValueChange={setTimeRange}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">Last 7 days</SelectItem>
                      <SelectItem value="30">Last 30 days</SelectItem>
                      <SelectItem value="90">Last 90 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  {vitals
                    .sort((a, b) => new Date(b.measuredAt) - new Date(a.measuredAt))
                    .slice(0, 10)
                    .map((vital) => {
                      const vitalInfo = vitalTypes.find(vt => vt.value === vital.kind);
                      const status = getVitalStatus(vital.kind, vital.value);
                      
                      return (
                        <div key={vital.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-4">
                            <div className={`w-3 h-3 rounded-full ${
                              status.status === 'normal' ? 'bg-green-500' :
                              status.status === 'high' ? 'bg-red-500' :
                              status.status === 'borderline' ? 'bg-yellow-500' : 'bg-blue-500'
                            }`}></div>
                            <div>
                              <p className="font-medium text-gray-900">{vitalInfo?.label || vital.kind}</p>
                              <p className="text-sm text-gray-600">
                                {new Date(vital.measuredAt).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-semibold text-gray-900">
                              {vital.value} <span className="text-sm text-gray-500">{vital.unit}</span>
                            </p>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Add New Vital */}
            <Card className="border-green-200">
              <CardHeader className="bg-green-50">
                <CardTitle className="flex items-center space-x-2 text-green-700">
                  <Plus className="w-5 h-5" />
                  <span>Add New Reading</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <form onSubmit={addVital} className="space-y-4">
                  <Select value={newVital.kind} onValueChange={(value) => setNewVital({...newVital, kind: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select vital type" />
                    </SelectTrigger>
                    <SelectContent>
                      {vitalTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="Value"
                    value={newVital.value}
                    onChange={(e) => setNewVital({...newVital, value: e.target.value})}
                    required
                  />
                  
                  <Input
                    type="date"
                    value={newVital.measuredAt}
                    onChange={(e) => setNewVital({...newVital, measuredAt: e.target.value})}
                    required
                  />
                  
                  <Button type="submit" className="w-full bg-green-600 hover:bg-green-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Reading
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Health Tips */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Heart className="w-5 h-5 text-red-600" />
                  <span>Health Tips</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-medium text-blue-900 mb-2">Blood Pressure</h4>
                    <p className="text-sm text-blue-700">
                      Regular exercise and reduced sodium intake can help maintain healthy blood pressure.
                    </p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <h4 className="font-medium text-green-900 mb-2">Blood Sugar</h4>
                    <p className="text-sm text-green-700">
                      Monitor carbohydrate intake and maintain regular meal times for stable blood sugar.
                    </p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <h4 className="font-medium text-purple-900 mb-2">Cholesterol</h4>
                    <p className="text-sm text-purple-700">
                      Include fiber-rich foods and omega-3 fatty acids in your diet for heart health.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="bg-orange-50 border-orange-200">
              <CardContent className="p-6 text-center">
                <h3 className="font-semibold text-gray-900 mb-4">Need Medical Consultation?</h3>
                <div className="space-y-3">
                  <a 
                    href="https://wa.me/917337541113?text=Need consultation about my health vitals"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button className="w-full bg-orange-600 hover:bg-orange-700">
                      <Heart className="w-4 h-4 mr-2" />
                      Consult Doctor
                    </Button>
                  </a>
                  <Button variant="outline" className="w-full border-orange-200 text-orange-600">
                    <TrendingUp className="w-4 h-4 mr-2" />
                    View Trends
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Vitals;